/**
 * Colocación de la ventana flotante.
 *
 * Vive fuera del componente para poder probarlo: la ventana no se puede salir de la pantalla, y en
 * una pantalla estrecha **se estrecha** en vez de quedarse con las esquinas fuera de vista, que es
 * como se pierde el botón de cerrar.
 */
export interface PanelBox {
  x: number;
  y: number;
  width: number;
  height: number;
  viewportWidth: number;
  viewportHeight: number;
}

/** Margen mínimo que se deja siempre visible del borde de la pantalla. */
export const MARGEN = 8;

/** Ancho real: el pedido, o lo que quepa dejando margen a los dos lados. */
export function panelWidth(width: number, viewportWidth: number): number {
  return Math.max(200, Math.min(width, viewportWidth - MARGEN * 2));
}

/** Alto máximo: nunca más que la pantalla menos los márgenes. */
export function panelMaxHeight(viewportHeight: number): number {
  return Math.max(160, viewportHeight - MARGEN * 2);
}

export function clampPosition(box: PanelBox): { x: number; y: number } {
  const ancho = panelWidth(box.width, box.viewportWidth);
  const alto = Math.min(box.height, panelMaxHeight(box.viewportHeight));
  const maxX = Math.max(MARGEN, box.viewportWidth - ancho - MARGEN);
  const maxY = Math.max(MARGEN, box.viewportHeight - alto - MARGEN);
  return {
    x: Math.min(Math.max(MARGEN, box.x), maxX),
    y: Math.min(Math.max(MARGEN, box.y), maxY)
  };
}

/**
 * Posición inicial: abajo a la derecha. Arriba tapaba la columna de acciones de las primeras filas,
 * que es justo lo que el usuario acaba de pulsar.
 */
export function initialPosition(box: Omit<PanelBox, 'x' | 'y'>): { x: number; y: number } {
  const ancho = panelWidth(box.width, box.viewportWidth);
  const alto = Math.min(box.height, panelMaxHeight(box.viewportHeight));
  return clampPosition({
    ...box,
    x: box.viewportWidth - ancho - 24,
    y: box.viewportHeight - alto - 24
  });
}
